/**
 * Dynamic-columns dropdown for DataTable pages, extracted from the
 * Employees page so every table offers the same control.
 *
 *     const { visible, picker } = useColumns("employees.columns", [
 *       { id: "employee_id", label: "Employee ID" },
 *       { id: "name", label: "Name", locked: true },
 *       { id: "monthly_basic", label: "Monthly basic", defaultVisible: false },
 *     ]);
 *     // toolbar: {picker}
 *     // columns: COLUMN_OPTIONS.filter(c => visible.includes(c.id))…
 *
 * Selection persists per user via `useUiPreference` (server-side
 * `ui_state_json`), so it survives relogin and follows the user across
 * browsers. `defaultVisible: false` marks opt-in columns (sensitive or
 * niche); `locked: true` pins a column on.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Columns3 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useUiPreference } from "@/lib/use-ui-pref";
import { cn } from "@/lib/utils";

export interface ColumnOption {
  id: string;
  label: string;
  /** Always visible; can't be toggled off. */
  locked?: boolean;
  /** Defaults to true. Set false for opt-in columns. */
  defaultVisible?: boolean;
}

export function defaultVisibleColumns(
  options: ReadonlyArray<ColumnOption>,
): string[] {
  return options
    .filter((c) => c.locked || c.defaultVisible !== false)
    .map((c) => c.id);
}

export function useColumns(
  prefKey: string,
  options: ReadonlyArray<ColumnOption>,
): { visible: string[]; picker: ReactNode } {
  const [stored, setStored] = useUiPreference<string[]>(
    prefKey,
    defaultVisibleColumns(options),
  );

  // Drop ids that no longer exist; force locked columns on.
  const known = new Set(options.map((c) => c.id));
  const locked = options.filter((c) => c.locked).map((c) => c.id);
  const visible = [
    ...locked.filter((id) => !stored.includes(id)),
    ...stored.filter((id) => known.has(id)),
  ];

  return {
    visible,
    picker: (
      <ColumnsPicker options={options} visible={visible} onChange={setStored} />
    ),
  };
}

export function ColumnsPicker({
  options,
  visible,
  onChange,
}: {
  options: ReadonlyArray<ColumnOption>;
  visible: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  function toggle(id: string) {
    onChange(
      visible.includes(id)
        ? visible.filter((c) => c !== id)
        : [...visible, id],
    );
  }

  return (
    <div className="relative" ref={ref}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className="gap-1.5"
      >
        <Columns3 size={15} strokeWidth={1.5} aria-hidden="true" />
        Columns
      </Button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full z-20 mt-1 w-56 rounded-card border border-slate-200 bg-white p-2 shadow-lg"
        >
          <p className="px-2 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-wider text-slate">
            Show columns
          </p>
          {options.map((c) => (
            <label
              key={c.id}
              className={cn(
                "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-soft/60",
                c.locked && "cursor-default opacity-60",
              )}
            >
              <input
                type="checkbox"
                checked={visible.includes(c.id)}
                disabled={c.locked}
                onChange={() => toggle(c.id)}
                className="h-3.5 w-3.5"
              />
              {c.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
